import { useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { enableTraceBasedMetrics } from '@gonfalon/dogfood-flags/dogfood-flags';
import { LaunchDarklyLogoIcon } from '@gonfalon/icons/LaunchDarklyLogoIcon';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { skeletonStyles } from '@gonfalon/metric-events/Sources';
import { DataSourceType } from '@gonfalon/metrics/constants';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { MetricKind } from '@gonfalon/metrics/MetricKind';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, Label, Link, ListBox, ListBoxItem, Popover, Select, SelectValue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { useWarehouseManifests } from '../internal/CreateMetricModal/useWarehouseManifests';

import type { MetricForm } from './formUtils';

import styles from './EventSourceField.module.css';

const TRACES_SELECTION = 'traces';

const LD_HOSTED_DEFAULT = {
  key: DataSourceType.LAUNCHDARKLY_HOSTED,
};

export const EventSourceField = ({
  control,
  onChange,
  isDisabled,
  disableLDHosted,
}: {
  control: Control<MetricForm>;
  onChange?: ({
    dataSourceKey,
    kind,
  }: {
    dataSourceKey: MetricForm['dataSourceKey'];
    kind: Extract<MetricKind, MetricKind.TRACE | MetricKind.CUSTOM>;
  }) => void;
  isDisabled?: boolean;
  disableLDHosted?: boolean;
}) => {
  const projectKey = useProjectKey();
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const kindFormValue = useWatch({ control, name: 'kind' });
  const ratioOrSimpleFormValue = useWatch({ control, name: 'ratioOrSimple' });
  const { data } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const dataSources = useMemo(() => data?.items?.filter?.((ds) => Boolean(ds.environmentKey)), [data]);
  const warehouseManifests = useWarehouseManifests();
  const [lastSelectedValue, setLastSelectedValue] = useState<string | null>(null);

  const selectValue = useMemo(() => {
    if (kindFormValue === 'trace') {
      return TRACES_SELECTION;
    }

    if (isLDHosted(dataSourceKeyFormValue)) {
      return DataSourceType.LAUNCHDARKLY_HOSTED;
    }

    const selectedDataSource = dataSources?.find((ds) => ds.key === dataSourceKeyFormValue);
    const selectedWarehouse = warehouseManifests?.find(
      (manifest) => manifest?.key === selectedDataSource?.integrationKey,
    )?.key;

    return selectedWarehouse ?? lastSelectedValue ?? undefined;
  }, [dataSourceKeyFormValue, kindFormValue, dataSources, warehouseManifests, lastSelectedValue]);

  if (ratioOrSimpleFormValue === 'ratio') {
    return (
      <Select className={styles.eventSourceSelect} isDisabled>
        <Label>
          <Message id="metrics.metricForm.sourceLabel" />
        </Label>
        <Button>
          <Icon name="package-outline" size="small" />
          {msg('metrics.metricForm.warehouseNative')}
        </Button>
      </Select>
    );
  }

  return (
    <Select
      className={styles.eventSourceSelect}
      onChange={(value) => {
        setLastSelectedValue(String(value));
        switch (String(value)) {
          case DataSourceType.LAUNCHDARKLY_HOSTED:
            onChange?.({ dataSourceKey: LD_HOSTED_DEFAULT.key, kind: MetricKind.CUSTOM });
            break;
          case TRACES_SELECTION:
            onChange?.({ dataSourceKey: DataSourceType.LAUNCHDARKLY_HOSTED, kind: MetricKind.TRACE });
            break;
          default:
            if (typeof value === 'string') {
              const defaultDataSourceForWarehouse = dataSources?.find((ds) => ds.integrationKey === value);
              onChange?.({ dataSourceKey: defaultDataSourceForWarehouse?.key, kind: MetricKind.CUSTOM });
            }
            break;
        }
      }}
      value={selectValue ?? null}
      isDisabled={isDisabled}
    >
      <Label>
        <Message id="metrics.metricForm.sourceLabel" />
      </Label>
      <Button>
        <SelectValue className={styles.selectedOption} />
        <Icon name="chevron-down" size="small" />
      </Button>
      {kindFormValue === 'trace' && (
        <Link
          href={getDocumentationUrl('home/metrics/create-trace-metrics#creating-trace-metrics')}
          target="_blank"
          className={styles.learnMore}
        >
          <Message id="metrics.metricForm.learnMore" /> <Icon name="book-external-link" size="tiny" />
        </Link>
      )}
      <Popover>
        <ListBox disabledKeys={disableLDHosted ? [DataSourceType.LAUNCHDARKLY_HOSTED] : []}>
          <ListBoxItem id={DataSourceType.LAUNCHDARKLY_HOSTED} data-test-id="ld-data-source">
            <LaunchDarklyLogoIcon size="small" />
            {msg('metrics.metricForm.launchDarklyHosted')}
          </ListBoxItem>
          {warehouseManifests?.map((manifest, i) => {
            const key = `${i}-${manifest?.key}`;

            if (manifest) {
              return (
                <ListBoxItem id={manifest?.key} key={key} data-test-id="warehouse-data-source">
                  <img
                    src={manifest?.icons?.square}
                    alt={msg('metrics.metricForm.integrationLogoLabel', { name: manifest?.name ?? '' })}
                    className={styles.integrationIcon}
                  />
                  {msg('metrics.metricForm.warehouseNativeVendor', { vendor: manifest?.warehouseVendor ?? '' })}
                </ListBoxItem>
              );
            } else {
              return (
                <ListBoxItem className={styles.skeletonItem} key={key} data-test-id="warehouse-data-source" isDisabled>
                  <span className={clsx(skeletonStyles.skeleton, styles.integrationIcon, styles.rounded)}></span>
                  <span className={clsx(skeletonStyles.skeleton, styles.skeletonText)}>
                    <Message id="metrics.metricForm.warehouseNative" />
                  </span>
                </ListBoxItem>
              );
            }
          })}
          {enableTraceBasedMetrics() && (
            <ListBoxItem id={TRACES_SELECTION} data-test-id="traces-data-source">
              <Icon name="pulse" size="small" />
              {msg('metrics.metricForm.traces')}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
};
